import icon_photo from '@/assets/img/icon_photo.svg'
import icon_zip from '@/assets/img/icon_zip.svg'
import icon_word from '@/assets/img/icon_word.svg'
import icon_unknown from '@/assets/img/icon_unknown.svg'

export function getUrlParams(url: string) {
  try {
    const validURL = url.startsWith('?') ? `http://dummyhost${url}` : url
    const searchParams = new URLSearchParams(new URL(validURL).search)
    const paramsObject: Record<string, any> = {}

    for (const [key, value] of searchParams.entries()) {
      // 处理特殊字符串值
      if (value.toLowerCase() === 'null') {
        paramsObject[key] = null
      } else if (value.toLowerCase() === 'nan') {
        paramsObject[key] = NaN
      } else if (value.toLowerCase() === 'undefined') {
        paramsObject[key] = undefined
      } else {
        paramsObject[key] = value
      }
    }

    return paramsObject
  } catch (error) {
    console.error('Invalid URL format:', url)
    return {}
  }
}

// 解析后端 API 的 origin（协议 + 域名 + 端口，不含末尾斜杠；dev 下为相对路径走 vite proxy）。
//
// test / production：API 域名跟随当前前端页面域名 —— 取 hostname 第一段 label 追加 `-api`，
// 例如页面在 test-ticket.dvdfab.me 时 API 走 test-ticket-api.dvdfab.me。本应用会被 iframe 嵌入
// 并解析到多个域名，但后端是同一台服务器靠域名分流，因此 API 必须与前端同域名而非写死。
// 无法推导（localhost / IP 预览构建包）时回退到 VITE_BASE_PATH。
//
// development：VITE_BASE_PATH 为相对路径（/api），保持走 vite proxy，不做推导。
export function getApiOrigin(): string {
  const configured = import.meta.env.VITE_BASE_PATH || ''

  // dev：相对路径，直接走 proxy
  if (!/^https?:\/\//i.test(configured)) {
    return configured
  }

  // test / production：根据当前页面域名动态推导
  const { protocol, hostname, port } = window.location
  if (hostname && hostname !== 'localhost' && !/^\d+(\.\d+){3}$/.test(hostname)) {
    const labels = hostname.split('.')
    labels[0] = `${labels[0]}-api`
    const portPart = port ? `:${port}` : ''
    return `${protocol}//${labels.join('.')}${portPart}`
  }

  // 回退：env 写死域名
  return configured.replace(/\/$/, '')
}

export function formatDate(
  date: string | Date,
  showSeconds = false,
  t: (key: string) => string
) {
  const dateObj = new Date(date)
  const months = []
  for (let index = 1; index <= 12; index++) {
    months.push(t(`common.monthNameMap.${index}`))
  }
  const month = months[dateObj.getMonth()]
  const day = dateObj.getDate()
  const year = dateObj.getFullYear()
  if (showSeconds) {
    const hours = dateObj.getHours() < 10 ? `0${dateObj.getHours()}` : dateObj.getHours()
    const minutes = dateObj.getMinutes() < 10 ? `0${dateObj.getMinutes()}` : dateObj.getMinutes()
    const seconds = dateObj.getSeconds() < 10 ? `0${dateObj.getSeconds()}` : dateObj.getSeconds()
    return `${month} ${day}, ${year} ${hours}:${minutes}:${seconds}`
  } else return `${month} ${day}, ${year}`
}

// 传完整的 URL 或者 window.location.search
export function handleUrlParam(url: string, key: string, value: string) {
  try {
    const validURL = url.startsWith('http') ? url : `http://dummyhost${url}`
    const urlObj = new URL(validURL)
    if (urlObj.searchParams.has(key)) {
      urlObj.searchParams.set(key, value)
    } else {
      urlObj.searchParams.append(key, value)
    }
    return '?' + urlObj.toString().split('?')[1]
  } catch (error) {
    console.error('Invalid URL format:', url)
    return ''
  }
}

// 给父页面发送消息
export function sendMessageToParent(message: { type: string; text: string | number }) {
  if (window.parent) {
    window.parent.postMessage(message, '*')
  } else {
    console.error('Parent window not found.')
  }
}

// 获取页面高度
export function getPageHeight() {
  const height = Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
    document.body.clientHeight,
    document.documentElement.clientHeight
  )
  return height
}

// 获取文件的类型
// 根据错误提示，添加返回类型为相应的图标类型，这里假设为 string
export function getFileTypeIcon(file?: { filename?: string; file_name: string }): string {
  const extension = (file?.filename || file?.file_name)?.split('.')?.pop()?.toLowerCase() || ''
  if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
    return useResolvedImage(icon_photo).value
  } else if (['zip', 'rar', '7z'].includes(extension)) {
    return useResolvedImage(icon_zip).value
  } else if (['pdf', 'doc', 'docx', 'log'].includes(extension)) {
    return useResolvedImage(icon_word).value
  } else {
    return useResolvedImage(icon_unknown).value
  }
}
